import React, { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost } from '../lib/api';
import { inrCompact, inr, formatDate, statusColor } from '../lib/format';
import { Plus, Calculator, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useFormFieldHelpers } from '../hooks/useFormFieldHelpers';
import { useUrlTab } from '../lib/hooks/useUrlState';

const TABS = ['Subcontractor Master', 'Work Orders', 'RA Bills (Running Account)'];

export default function Subcontractors() {
  const [tab, setTab] = useUrlTab('tab', 'Subcontractor Master');
  return (
    <div>
      <PageHeader overline="Subcontractors" title="SC Master · Work Orders · RA Bills"
        subtitle="Empanelment, work order management, and RA bill processing with auto-deduction (advance, retention, TDS, GST, penalty) and certified amount calculation." />
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map(t => (
            <button key={t} data-testid={`sc-tab-${t.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`} onClick={() => setTab(t)}
              className={`relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap ${tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]'}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#3b4872]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Subcontractor Master' && <Master />}
          {tab === 'Work Orders' && <WorkOrders />}
          {tab === 'RA Bills (Running Account)' && <RABills />}
        </div>
      </div>
    </div>
  );
}

function Master() {
  const [items, setItems] = useState([]);
  useEffect(() => { apiGet('/subcontractors').then(setItems); }, []);
  return (
    <div>
      <div className="kpi-overline mb-3">Empanelled Subcontractors · {items.length}</div>
      <table className="w-full dense-table">
        <thead><tr><th>Name</th><th>Specialty</th><th>GSTIN</th><th>Rating</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="4" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No subcontractors</td></tr>}
          {items.map(s => (
            <tr key={s.id}>
              <td className="font-display font-semibold">{s.name}</td>
              <td>{s.specialty}</td>
              <td className="font-mono text-xs">{s.gst}</td>
              <td className="font-display font-bold">{s.rating?.toFixed(1) || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkOrders() {
  const [items, setItems] = useState([]);
  useEffect(() => { apiGet('/work-orders').then(d => setItems(d?.items || [])); }, []);
  return (
    <div>
      <div className="kpi-overline mb-3">Work Orders · {items.length}</div>
      <table className="w-full dense-table">
        <thead><tr><th>WO No</th><th>Subcontractor</th><th>Project</th><th>Scope</th><th className="text-right">Value</th><th className="text-right">Advance</th><th>Period</th><th>Status</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="8" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No work orders</td></tr>}
          {items.map(w => (
            <tr key={w.id}>
              <td className="font-mono text-xs">{w.wo_no}</td>
              <td>{w.subcontractor}</td>
              <td className="text-xs">{w.project_id?.slice(0, 8) || '—'}</td>
              <td className="max-w-md truncate text-xs">{w.scope}</td>
              <td className="text-right tabular-nums font-display font-bold">{inrCompact(w.value)}</td>
              <td className="text-right tabular-nums">{inrCompact(w.advance_paid)}</td>
              <td className="text-xs">{formatDate(w.start_date)} – {formatDate(w.end_date)}</td>
              <td><span className={`tag ${statusColor(w.status)}`}>{w.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RABills() {
  const [bills, setBills] = useState([]);
  const [wos, setWos] = useState([]);
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setBills(await apiGet('/ra-bills'));
    setWos((await apiGet('/work-orders'))?.items || []);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Running Account Bills</div>
          <h3 className="font-display font-bold text-base mt-1">{bills.length} bills · {bills.filter(b => b.status === 'Pending').length} pending approval</h3>
        </div>
        <button data-testid="ra-new-btn" onClick={() => setCreating(true)} className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm hover:bg-[#3b4872] font-display font-semibold"><Plus size={13} /> New RA Bill</button>
      </div>

      <table className="w-full dense-table">
        <thead><tr>
          <th>Bill</th><th>WO</th><th>Subcontractor</th><th>Period</th>
          <th className="text-right">Gross Cumulative</th>
          <th className="text-right">This Bill</th>
          <th className="text-right">Deductions</th>
          <th className="text-right">Net Payable</th>
          <th>Status</th><th></th>
        </tr></thead>
        <tbody>
          {bills.length === 0 && <tr><td colSpan="10" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No RA bills yet</td></tr>}
          {bills.map(b => {
            const ded = (b.advance_recovery || 0) + (b.retention || 0) + (b.tds || 0) + (b.penalty || 0) + (b.other_deductions || 0);
            return (
              <tr key={b.id}>
                <td className="font-display font-bold">{b.bill_no}</td>
                <td className="font-mono text-xs">{b.wo_no}</td>
                <td>{b.subcontractor}</td>
                <td className="text-xs">{formatDate(b.period_to)}</td>
                <td className="text-right tabular-nums">{inrCompact(b.gross_cumulative)}</td>
                <td className="text-right tabular-nums font-display font-bold">{inrCompact(b.this_bill_value)}</td>
                <td className="text-right tabular-nums text-red-600">({inrCompact(ded).replace('₹ ','')})</td>
                <td className="text-right tabular-nums font-display font-extrabold text-emerald-700">{inrCompact(b.net_payable)}</td>
                <td><span className={`tag ${statusColor(b.status)}`}>{b.status}</span></td>
                <td><button data-testid="ra-view-btn" onClick={() => setOpen(b)} className="text-xs text-[#3b4872] font-display font-semibold flex items-center gap-1"><FileText size={11} />View</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {open && <RABillDetail bill={open} onClose={() => setOpen(null)} />}
      {creating && <RABillCreate wos={wos} bills={bills} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function Row({ label, value, indent, negative, total }) {
  return (
    <div className={`flex items-center justify-between py-2 px-4 border-b border-slate-100 ${total ? 'bg-indigo-50/50 border-t-2 border-t-[#3b4872]' : ''} ${indent ? 'pl-8' : ''}`}>
      <div className={`text-sm ${total ? 'font-display font-extrabold' : indent ? 'text-slate-600' : 'font-display font-bold'}`}>{label}</div>
      <div className={`font-display tabular-nums ${total ? 'text-xl font-extrabold text-emerald-700' : 'font-bold'} ${negative ? 'text-red-600' : ''}`}>
        {negative ? '(' : ''}{inr(Math.abs(value || 0))}{negative ? ')' : ''}
      </div>
    </div>
  );
}

function RABillDetail({ bill, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{bill.bill_no} · {bill.wo_no}</div>
            <h3 className="font-display font-bold text-xl mt-1">RA Bill — {bill.subcontractor}</h3>
            <div className="text-xs text-slate-500 mt-1">Period upto: {formatDate(bill.period_to)} · <span className={`tag ${statusColor(bill.status)}`}>{bill.status}</span></div>
          </div>
          <button onClick={onClose} className="w-9 h-9 hover:bg-slate-100 rounded-sm flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="border border-slate-200 rounded-sm">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 kpi-overline">Bill Computation</div>
            <Row label="Gross Cumulative Work Done" value={bill.gross_cumulative} />
            <Row label="Less: Cumulative Previous Bills" value={bill.cumulative_previous} indent negative />
            <Row label="This Bill Value" value={bill.this_bill_value} />
            <Row label="Add: GST @ 18%" value={bill.gst} indent />
            <div className="border-t border-slate-200" />
            <Row label="Less: Advance Recovery" value={bill.advance_recovery} indent negative />
            <Row label="Less: Retention @ 5%" value={bill.retention} indent negative />
            <Row label="Less: TDS @ 2%" value={bill.tds} indent negative />
            <Row label="Less: Penalty / LD" value={bill.penalty} indent negative />
            <Row label="Less: Other Deductions" value={bill.other_deductions} indent negative />
            <Row label="Net Amount Payable" value={bill.net_payable} total />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <div className="kpi-overline text-emerald-700">Certified Amount</div>
              <div className="font-display text-2xl font-extrabold mt-1">{inr(bill.certified_amount)}</div>
              <div className="text-xs text-slate-600 mt-1">For client back-to-back billing</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <div className="kpi-overline text-amber-700">Retention Held</div>
              <div className="font-display text-2xl font-extrabold mt-1">{inr(bill.retention)}</div>
              <div className="text-xs text-slate-600 mt-1">Released after defect liability</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RABillCreate({ wos, bills, onClose, onSaved }) {
  const ff = useFormFieldHelpers('ra_bill_create');
  const [form, setForm] = useState({
    wo_id: wos[0]?.id || '',
    bill_no: `RA-${String(bills.length + 1).padStart(2, '0')}`,
    period_to: new Date().toISOString().slice(0, 10),
    gross_value: '',
    advance_recovery_pct: 10,
    retention_pct: 5,
    tds_pct: 2,
    gst_pct: 18,
    penalty: 0,
    other_deductions: 0,
  });
  const [computed, setComputed] = useState(null);
  const [boqItems, setBoqItems] = useState([]);
  const [lines, setLines] = useState([]);   // [{ boq_id, qty }]
  const wo = wos.find(w => w.id === form.wo_id);
  const cumulativePrev = bills.filter(b => b.wo_id === form.wo_id).reduce((s, b) => Math.max(s, b.gross_cumulative || 0), 0);
  const advanceOutstanding = (wo?.advance_paid || 0) - bills.filter(b => b.wo_id === form.wo_id).reduce((s, b) => s + (b.advance_recovery || 0), 0);

  // Load the project BOQ for the selected WO so the user can bill per line item.
  useEffect(() => {
    const pid = wo?.project_id;
    if (!pid) { setBoqItems([]); return; }
    apiGet(`/boq/by-project/${pid}`).then(d => setBoqItems((d.items || []).filter(i => !i.is_group))).catch(() => setBoqItems([]));
  }, [wo?.project_id]);

  const boqById = Object.fromEntries(boqItems.map(i => [i.id, i]));
  const builtItems = lines
    .filter(l => l.boq_id && Number(l.qty) > 0)
    .map(l => {
      const it = boqById[l.boq_id] || {};
      const qty = Number(l.qty);
      const rate = Number(it.rate || 0);
      return { boq_id: l.boq_id, item_code: it.item_code, description: it.description, unit: it.unit, qty, rate, amount: Math.round(qty * rate * 100) / 100 };
    });
  const linesTotal = builtItems.reduce((s, i) => s + i.amount, 0);

  const addLine = () => setLines(ls => [...ls, { boq_id: '', qty: '' }]);
  const updateLine = (idx, key, val) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, [key]: val } : l));
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));
  // When BOQ lines are used, the cumulative gross = previous + this period's lines total.
  const applyLinesToGross = () => {
    const newGross = cumulativePrev + linesTotal;
    setForm(f => ({ ...f, gross_value: String(newGross) }));
    setComputed(null);
  };

  const compute = async () => {
    const r = await apiPost('/ra-bills/compute', {
      gross_value: Number(form.gross_value),
      cumulative_previous: cumulativePrev,
      advance_outstanding: advanceOutstanding,
      advance_recovery_pct: Number(form.advance_recovery_pct),
      retention_pct: Number(form.retention_pct),
      tds_pct: Number(form.tds_pct),
      gst_pct: Number(form.gst_pct),
      penalty: Number(form.penalty),
      other_deductions: Number(form.other_deductions),
    });
    setComputed(r);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!computed) { toast.error('Click Compute first'); return; }
    await apiPost('/ra-bills', {
      bill_no: form.bill_no,
      wo_id: form.wo_id,
      wo_no: wo?.wo_no,
      subcontractor: wo?.subcontractor,
      project_id: wo?.project_id,
      period_to: form.period_to,
      gross_cumulative: Number(form.gross_value),
      cumulative_previous: cumulativePrev,
      items: builtItems,   // per-BOQ-item quantities → feeds the BOQ monthly billing view
      ...computed,
      status: 'Pending',
    });
    toast.success('RA Bill submitted for approval');
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-200 sticky top-0 bg-white">
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div>
          <h3 className="font-display font-bold text-xl mt-1">RA Bill — Auto-deduction Calculator</h3>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <Field label="Work Order">
            <select required value={form.wo_id} onChange={e => setForm({ ...form, wo_id: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
              {wos.map(w => <option key={w.id} value={w.id}>{w.wo_no} — {w.subcontractor}</option>)}
            </select>
          </Field>
          <Field label={`${ff.labelOf('bill_no','Bill No')}${ff.reqStar('bill_no', true)}`}><input value={form.bill_no} onChange={e => setForm({ ...form, bill_no: e.target.value })} required={ff.isReq('bill_no', true)} className={inputCls} /></Field>
          <Field label={`${ff.labelOf('period_to','Period upto')}${ff.reqStar('period_to', true)}`}><input type="date" value={form.period_to} onChange={e => setForm({ ...form, period_to: e.target.value })} required={ff.isReq('period_to', true)} className={inputCls} /></Field>
          <Field label="Gross Cumulative Value (₹)">
            <input required type="number" value={form.gross_value} onChange={e => { setForm({ ...form, gross_value: e.target.value }); setComputed(null); }} placeholder={`Min ${inrCompact(cumulativePrev)}`} className={inputCls} />
          </Field>
          {wo && (
            <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs grid grid-cols-3 gap-3">
              <div><div className="kpi-overline">WO Value</div><div className="font-display font-bold">{inrCompact(wo.value)}</div></div>
              <div><div className="kpi-overline">Cumulative Previous</div><div className="font-display font-bold">{inrCompact(cumulativePrev)}</div></div>
              <div><div className="kpi-overline">Advance Outstanding</div><div className="font-display font-bold">{inrCompact(advanceOutstanding)}</div></div>
            </div>
          )}
          <Field label="Advance Recovery %"><input type="number" value={form.advance_recovery_pct} onChange={e => { setForm({ ...form, advance_recovery_pct: e.target.value }); setComputed(null); }} className={inputCls} /></Field>
          <Field label="Retention %"><input type="number" value={form.retention_pct} onChange={e => { setForm({ ...form, retention_pct: e.target.value }); setComputed(null); }} className={inputCls} /></Field>
          <Field label="TDS %"><input type="number" value={form.tds_pct} onChange={e => { setForm({ ...form, tds_pct: e.target.value }); setComputed(null); }} className={inputCls} /></Field>
          <Field label="GST %"><input type="number" value={form.gst_pct} onChange={e => { setForm({ ...form, gst_pct: e.target.value }); setComputed(null); }} className={inputCls} /></Field>
          <Field label="Penalty / LD (₹)"><input type="number" value={form.penalty} onChange={e => { setForm({ ...form, penalty: e.target.value }); setComputed(null); }} className={inputCls} /></Field>
          <Field label="Other Deductions (₹)"><input type="number" value={form.other_deductions} onChange={e => { setForm({ ...form, other_deductions: e.target.value }); setComputed(null); }} className={inputCls} /></Field>

          {/* BOQ line items billed this period — feeds the BOQ Monthly Billing view */}
          {wo?.project_id && (
            <div className="col-span-2 border border-slate-200 rounded-sm" data-testid="ra-boq-lines">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <span className="kpi-overline">BOQ Items Billed This Period (recommended)</span>
                <button type="button" onClick={addLine} data-testid="ra-add-boq-line" className="text-[#3b4872] text-xs font-display font-semibold flex items-center gap-1"><Plus size={12} /> Add line</button>
              </div>
              <div className="p-3 space-y-2">
                {lines.length === 0 && <p className="text-[11px] text-slate-400">Add the BOQ items billed in this RA so client billing quantities are tracked per item. Leave empty to bill by gross value only.</p>}
                {lines.map((l, idx) => {
                  const it = boqById[l.boq_id];
                  return (
                    <div key={idx} className="flex items-center gap-2" data-testid={`ra-boq-line-${idx}`}>
                      <select value={l.boq_id} onChange={e => updateLine(idx, 'boq_id', e.target.value)} className="flex-1 px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white" data-testid={`ra-boq-line-item-${idx}`}>
                        <option value="">— Select BOQ item —</option>
                        {boqItems.map(b => <option key={b.id} value={b.id}>{b.item_code} · {(b.description || '').slice(0, 50)}</option>)}
                      </select>
                      <input type="number" step="any" value={l.qty} onChange={e => updateLine(idx, 'qty', e.target.value)} placeholder="Qty" className="w-24 px-2 py-1.5 border border-slate-300 rounded-sm text-xs tabular-nums" data-testid={`ra-boq-line-qty-${idx}`} />
                      <span className="w-12 text-[11px] text-slate-500">{it?.unit || ''}</span>
                      <span className="w-24 text-right text-xs tabular-nums font-semibold">{it && Number(l.qty) > 0 ? inrCompact(Number(l.qty) * Number(it.rate || 0)) : '—'}</span>
                      <button type="button" onClick={() => removeLine(idx)} className="text-slate-400 hover:text-rose-600"><X size={14} /></button>
                    </div>
                  );
                })}
                {builtItems.length > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-xs">
                    <span className="font-display font-semibold">Lines total: <span className="tabular-nums">{inrCompact(linesTotal)}</span></span>
                    <button type="button" onClick={applyLinesToGross} data-testid="ra-apply-lines" className="px-3 py-1 bg-[#3b4872] text-white hover:bg-[#2d3856] rounded-sm font-display font-semibold hover:bg-[#3b4872]">Set gross = previous + lines</button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="col-span-2 flex justify-end">
            <button type="button" data-testid="ra-compute-btn" onClick={compute} className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white hover:bg-[#2d3856] text-sm rounded-sm hover:bg-[#3b4872] font-display font-semibold"><Calculator size={14} /> Compute</button>
          </div>

          {computed && (
            <div className="col-span-2 border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 kpi-overline">Computed Bill</div>
              <Row label="This Bill Value" value={computed.this_bill_value} />
              <Row label="Add: GST" value={computed.gst} indent />
              <Row label="Less: Advance Recovery" value={computed.advance_recovery} indent negative />
              <Row label="Less: Retention" value={computed.retention} indent negative />
              <Row label="Less: TDS" value={computed.tds} indent negative />
              {computed.penalty > 0 && <Row label="Less: Penalty" value={computed.penalty} indent negative />}
              {computed.other_deductions > 0 && <Row label="Less: Other" value={computed.other_deductions} indent negative />}
              <Row label="Net Amount Payable" value={computed.net_payable} total />
            </div>
          )}

          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200 sticky bottom-0 bg-white">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
            <button data-testid="ra-submit-btn" type="submit" disabled={!computed} className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">Submit Bill</button>
          </div>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
